// client/src/pages/wholesaler/AcceptInvite.js
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Users, Lock, Eye, EyeOff, Loader2 } from 'lucide-react';
import { wholesalerAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const AcceptInvite = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation('wholesaler');
  const { login: authLogin } = useAuth();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError(t('team.acceptInvite.passwordMismatch', 'Passwords do not match'));
      return;
    }

    if (password.length < 8) {
      setError(t('team.acceptInvite.passwordTooShort', 'Password must be at least 8 characters'));
      return;
    }

    setSubmitting(true);

    try {
      const res = await wholesalerAPI.acceptInvite(token, { password, name: name || undefined });
      const { token: authToken, user } = res.data;

      // Store token and user, then redirect
      localStorage.setItem('token', authToken);
      localStorage.setItem('user', JSON.stringify(user));
      setSuccess(true);

      // Redirect to dashboard after brief delay
      setTimeout(() => {
        window.location.href = '/wholesaler/dashboard';
      }, 1500);
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to accept invitation';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Users size={28} className="text-green-600" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">
            {t('team.acceptInvite.successTitle', 'Welcome to the Team!')}
          </h1>
          <p className="text-gray-500">
            {t('team.acceptInvite.successMessage', 'Redirecting to your dashboard...')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Users size={28} className="text-purple-600" />
          </div>
          <h1 className="text-xl font-bold text-gray-900">
            {t('team.acceptInvite.title', 'Join Team on WholesaleHub')}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {t('team.acceptInvite.subtitle', 'Set up your account to get started')}
          </p>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('team.acceptInvite.nameLabel', 'Your Name')}
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder={t('team.acceptInvite.namePlaceholder', 'John Doe')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Lock size={14} className="inline mr-1" />
              {t('team.acceptInvite.passwordLabel', 'Password')}
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-10 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="********"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-1">
              {t('team.acceptInvite.passwordHint', 'Must contain uppercase, lowercase, number, and special character')}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('team.acceptInvite.confirmPasswordLabel', 'Confirm Password')}
            </label>
            <input
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="********"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 font-medium text-sm flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                {t('team.acceptInvite.accepting', 'Accepting...')}
              </>
            ) : (
              t('team.acceptInvite.accept', 'Accept Invitation')
            )}
          </button>
        </form>

        {/* Footer */}
        <p className="text-center text-xs text-gray-400 mt-6">
          {t('team.acceptInvite.alreadyHaveAccount', 'Already have an account?')}{' '}
          <a href="/wholesaler/login" className="text-purple-600 hover:underline">
            {t('team.acceptInvite.loginLink', 'Log in')}
          </a>
        </p>
      </div>
    </div>
  );
};

export default AcceptInvite;
